/**
 * Organization (tenant) models for the System Admin portal.
 *
 * The backend still exposes organizations as "companies" (`/v1/company`) and only stores the
 * basic profile. Everything else here (admin, modules, configurations, integrations) is the
 * shape the revamp needs — see the TODOs in `OrganizationService`.
 */

export enum OrganizationModuleKey {
  AccountsPayable = 'accountsPayable',
  ExpenseManagement = 'expenseManagement',
  RepairAndMaintenance = 'repairAndMaintenance',
  MealDynamicsAi = 'mealDynamicsAi',
}

export interface OrganizationModuleOption {
  key: OrganizationModuleKey;
  name: string;
  description: string;
  icon: string;
  /** Icon tile colours. */
  iconColor: string;
  iconBackground: string;
  /** Label of the "sync to Accounts Payable" toggle, when the module has one. */
  syncLabel?: string;
  /** Text under the sync toggle. */
  syncDescription?: string;
}

export const ORGANIZATION_MODULE_OPTIONS: readonly OrganizationModuleOption[] = [
  {
    key: OrganizationModuleKey.AccountsPayable,
    name: 'Accounts Payable',
    description: 'Manage vendor bills, approvals, and payments in one place.',
    icon: 'ri-file-list-3-line',
    iconColor: '#ffffff',
    iconBackground: '#06b6d4',
  },
  {
    key: OrganizationModuleKey.ExpenseManagement,
    name: 'Expense Management',
    description: 'Capture, categorize, approve and process employee expenses.',
    icon: 'ri-wallet-3-line',
    iconColor: '#ffffff',
    iconBackground: '#2970ff',
    syncLabel: 'Sync expenses to Accounts Payable',
    syncDescription: 'Automatically route approved expenses into your AP workflow.',
  },
  {
    key: OrganizationModuleKey.RepairAndMaintenance,
    name: 'Repair & Maintenance',
    description: 'Log work orders, vendor jobs, and maintenance costs per location.',
    icon: 'ri-tools-line',
    iconColor: '#ffffff',
    iconBackground: '#f5a524',
    syncLabel: 'Sync R&M costs to Accounts Payable',
    syncDescription: 'Automatically route repair & maintenance costs into your AP workflow.',
  },
  {
    key: OrganizationModuleKey.MealDynamicsAi,
    name: 'Meal Dynamics AI',
    description: "An AI agent that drafts bills from invoices, and answers your team's questions about the platform.",
    icon: 'ri-magic-line',
    iconColor: '#6964e1',
    iconBackground: '#f0f0ff',
  },
];

export enum AccountingPeriodFormat {
  CalendarMonths = 'calendarMonths',
  FourWeeks = 'fourWeeks',
  FourFourFiveWeeks = 'fourFourFiveWeeks',
}

export const ACCOUNTING_PERIOD_FORMAT_OPTIONS: readonly { label: string; value: AccountingPeriodFormat }[] = [
  { label: 'Calendar Months', value: AccountingPeriodFormat.CalendarMonths },
  { label: '4 Weeks', value: AccountingPeriodFormat.FourWeeks },
  { label: '4-4-5 Weeks', value: AccountingPeriodFormat.FourFourFiveWeeks },
];

export enum AccountingSoftware {
  NetSuite = 'netSuite',
  QuickBooks = 'quickBooks',
  Xero = 'xero',
}

export interface AccountingSoftwareOption {
  key: AccountingSoftware;
  name: string;
  description: string;
  logo: string;
  logoColor: string;
  logoBackground: string;
  /** Shown but not selectable yet. */
  disabled?: boolean;
}

export const ACCOUNTING_SOFTWARE_OPTIONS: readonly AccountingSoftwareOption[] = [
  {
    key: AccountingSoftware.NetSuite,
    name: 'NetSuite',
    description: 'Cloud ERP for accounting, finance, inventory, and operations.',
    logo: 'ri-links-line',
    logoColor: '#1f4b6e',
    logoBackground: '#e8f1f8',
  },
  {
    key: AccountingSoftware.QuickBooks,
    // TODO: enable once the QuickBooks OAuth connection is available.
    disabled: true,
    name: 'Quickbooks',
    description: 'Simple accounting for invoicing, expenses, payroll, and reporting.',
    logo: 'ri-copper-coin-line',
    logoColor: '#ffffff',
    logoBackground: '#2ca01c',
  },
  {
    key: AccountingSoftware.Xero,
    // TODO: enable once the Xero OAuth connection is available.
    disabled: true,
    name: 'Xero 2.0',
    description: 'Cloud accounting for invoicing, expenses, and reporting.',
    logo: 'ri-cloud-line',
    logoColor: '#ffffff',
    logoBackground: '#13b5ea',
  },
];

/** NetSuite token-based authentication signature methods. */
export const NETSUITE_SIGNATURE_METHODS: readonly string[] = ['HMAC-SHA256', 'HMAC-SHA1'];

export interface NetSuiteCredentials {
  baseUrl: string;
  signatureMethod: string;
  consumerKey: string;
  consumerSecret: string;
  tokenSecret: string;
  accessToken: string;
  realm: string;
}

/** NetSuiteConnectionTestModel: the result of Test Connection. */
export interface NetSuiteConnectionTestResult {
  isConnected: boolean;
  statusCode: number | null;
  message: string | null;
}

export interface OrganizationAccountingIntegration {
  software: AccountingSoftware;
  /** Only for NetSuite; QuickBooks and Xero connect through OAuth. */
  netSuite?: NetSuiteCredentials;
}

export enum DedicatedEmailStatus {
  PendingConfirmation = 'pendingConfirmation',
  RequestExpired = 'requestExpired',
  Connected = 'connected',
}

export interface DedicatedEmail {
  /** Mailbox id from the API; set once the email is saved on an organization. */
  id?: string;
  email: string;
  provider: string;
  /** Unknown for saved emails until the API returns it. */
  status?: DedicatedEmailStatus;
}

/** The user an organization's email invoices are created for; the drafts show them as creator. */
export interface EmailToApDraftActor {
  userId: string;
  name: string;
  email: string;
}

export interface PhoneNumberValue {
  /** ISO country code, e.g. "US". */
  countryCode: string;
  /** Dial code without "+", e.g. "1". */
  dialCode: string;
  number: string;
}

export interface OrganizationAddress {
  addressLine1?: string | null;
  addressLine2?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  zip?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface OrganizationAdminRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: PhoneNumberValue;
  password: string;
}

export interface OrganizationModuleSelection {
  key: OrganizationModuleKey;
  /** Sync to Accounts Payable; only meaningful for Expense Management and R&M. */
  syncToAccountsPayable: boolean;
}

export interface OrganizationCreateRequest {
  name: string;
  timeZone: string;
  country: string;
  website?: string | null;
  email?: string | null;
  phone?: PhoneNumberValue | null;
  logoUrl?: string | null;
  admin: OrganizationAdminRequest;
  address?: OrganizationAddress | null;
  modules: OrganizationModuleSelection[];
  accountingPeriodFormat?: AccountingPeriodFormat | null;
  accountingIntegration?: OrganizationAccountingIntegration | null;
  dedicatedEmails: string[];
}

/** Display name of the mailbox provider, from the email domain. */
export function resolveEmailProvider(email: string): string {
  const domain = (email.split('@')[1] ?? '').toLowerCase();
  if (domain === 'gmail.com' || domain === 'googlemail.com') return 'Gmail';
  if (['outlook.com', 'hotmail.com', 'live.com', 'msn.com'].includes(domain)) return 'Outlook';
  if (domain.startsWith('yahoo.')) return 'Yahoo';
  if (domain === 'icloud.com' || domain === 'me.com') return 'iCloud';
  return 'Email';
}

/** Formats a phone value for display, e.g. "+1 (212) 555-8400". */
export function formatPhoneNumber(phone: PhoneNumberValue | null | undefined): string {
  if (!phone?.number) return '';
  return phone.dialCode ? `+${phone.dialCode} ${phone.number}` : phone.number;
}

// ── Listing ─────────────────────────────────────────────────────────────────

export interface OrganizationListItem {
  id: string;
  name: string;
  logoUrl?: string | null;
  adminName?: string | null;
  adminAvatarUrl?: string | null;
  accountingSoftware?: AccountingSoftware | null;
  /** Module display names, in the order the backend returns them. */
  modules: string[];
  isActive: boolean;
  createdOn?: Date | string | null;
}

export interface OrganizationSearchRequest {
  keyword?: string;
  page: number;
  pageSize: number;
  accountingSoftware?: AccountingSoftware | null;
  includeInactive: boolean;
}

export function accountingSoftwareName(software: AccountingSoftware | null | undefined): string {
  return ACCOUNTING_SOFTWARE_OPTIONS.find((x) => x.key === software)?.name ?? '';
}

/** Up to two initials, e.g. "Acme Corp" → "AC". */
export function nameInitials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
}

// ── Organization Settings ───────────────────────────────────────────────────

/** Organization Settings (profile menu): the detail page of the signed-in user's own organization. */
export const ORGANIZATION_SETTINGS_URL = '/companies/settings';
/** /my pagePermissions entry ("OrganizationSettings" menu) that grants Organization Settings. */
export const ORGANIZATION_SETTINGS_PAGE_URL = 'companies';

// ── Detail ──────────────────────────────────────────────────────────────────

export interface OrganizationModuleState {
  key: OrganizationModuleKey;
  isActive: boolean;
  /** Sync to Accounts Payable; only for Expense Management and R&M. */
  syncToAccountsPayable: boolean;
}

export interface OrganizationConfigurations {
  bypassApManager1: boolean;
  vendorInviteExpiryDays: number | null;
  externalVendorInviteExpiryDays: number | null;
  vendorPaymentDetailsExpiryDays: number | null;
  createEmailToApDraft: boolean;
  allowMealDynamicsAi: boolean;
  implementRolesAndPermissions: boolean;
}

export interface OrganizationAdminSummary {
  userId?: string | null;
  name: string;
  avatarUrl?: string | null;
}

export interface OrganizationDetail {
  id: string;
  name: string;
  logoUrl?: string | null;
  createdOn?: Date | string | null;
  isActive: boolean;
  website?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: PhoneNumberValue | null;
  timeZone?: string | null;
  admin?: OrganizationAdminSummary | null;
  address: OrganizationAddress;
  modules: OrganizationModuleState[];
  configurations: OrganizationConfigurations;
  accountingPeriodFormat?: AccountingPeriodFormat | null;
  accountingIntegration?: OrganizationAccountingIntegration | null;
  dedicatedEmails: DedicatedEmail[];
  /** Null when nobody qualifies, in which case Create Email to AP Draft cannot be turned on. */
  emailToApDraftActor?: EmailToApDraftActor | null;
}

export interface OrganizationBasicDetailsUpdate {
  website?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: PhoneNumberValue | null;
  timeZone?: string | null;
}

export const DEFAULT_ORGANIZATION_CONFIGURATIONS: OrganizationConfigurations = {
  bypassApManager1: false,
  vendorInviteExpiryDays: null,
  externalVendorInviteExpiryDays: null,
  vendorPaymentDetailsExpiryDays: null,
  createEmailToApDraft: false,
  allowMealDynamicsAi: false,
  implementRolesAndPermissions: false,
};

export function moduleOption(key: OrganizationModuleKey): OrganizationModuleOption | undefined {
  return ORGANIZATION_MODULE_OPTIONS.find((m) => m.key === key);
}

/**
 * Character limits for organization fields, the same as the vendor form uses
 * (name/email/city 50, address lines 500, zip 30, password 30). Website follows the company form.
 */
/** Organization profile photo: PNG/JPEG up to 25 MB. */
export const ORGANIZATION_LOGO_TYPES: readonly string[] = ['image/png', 'image/jpeg'];
export const ORGANIZATION_LOGO_MAX_MB = 25;

const INVALID_LOGO_MESSAGE = 'Please upload a valid PNG or JPEG image.';
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];

/**
 * Why a picked profile photo can't be used, or null when it's fine. Checks the type and size, then the
 * file's own bytes (PNG/JPEG signature) and that the browser can decode it, so a renamed non-image
 * file is rejected instead of showing a broken preview.
 */
export async function organizationLogoError(file: File): Promise<string | null> {
  if (!ORGANIZATION_LOGO_TYPES.includes(file.type)) {
    return 'Please upload a PNG or JPEG image.';
  }
  if (file.size > ORGANIZATION_LOGO_MAX_MB * 1024 * 1024) {
    return `Image size must not exceed ${ORGANIZATION_LOGO_MAX_MB} MB.`;
  }
  try {
    const head = new Uint8Array(await file.slice(0, PNG_SIGNATURE.length).arrayBuffer());
    const startsWith = (signature: number[]) => signature.every((byte, i) => head[i] === byte);
    if (!startsWith(PNG_SIGNATURE) && !startsWith(JPEG_SIGNATURE)) {
      return INVALID_LOGO_MESSAGE;
    }
    return (await canDecodeImage(file)) ? null : INVALID_LOGO_MESSAGE;
  } catch {
    return INVALID_LOGO_MESSAGE;
  }
}

function canDecodeImage(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const done = (ok: boolean) => {
      URL.revokeObjectURL(url);
      resolve(ok);
    };
    img.onload = () => done(img.naturalWidth > 0 && img.naturalHeight > 0);
    img.onerror = () => done(false);
    img.src = url;
  });
}

export const ORGANIZATION_FIELD_LIMITS = {
  name: 50,
  website: 50,
  email: 50,
  firstName: 50,
  lastName: 50,
  password: 30,
  addressLine1: 500,
  addressLine2: 500,
  city: 50,
  zip: 30,
} as const;
